"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OutputChannelObserver = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const StringConstants_1 = require("../Acquisition/StringConstants");
const EventStreamEvents_1 = require("./EventStreamEvents");
const EventType_1 = require("./EventType");
class OutputChannelObserver {
    constructor(outputChannel, suppressOutput = false, highVerbosity = false) {
        this.outputChannel = outputChannel;
        this.suppressOutput = suppressOutput;
        this.highVerbosity = highVerbosity;
        this.inProgressDownloads = [];
        this.hasContent = false;
    }
    post(event) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (this.suppressOutput || ((event === null || event === void 0 ? void 0 : event.verboseOutputOnly) && !this.highVerbosity)) {
            return;
        }
        switch (event.type) {
            case EventType_1.EventType.DotnetAcquisitionStart:
                const acquisitionStarted = event;
                this.inProgressDownloads.push(acquisitionStarted.install.installId);
                this.appendOutput(`${acquisitionStarted.requestingExtensionId} requested to download the ${acquisitionStarted.install.installMode === 'sdk' ? '.NET SDK' :
                    acquisitionStarted.install.installMode === 'runtime' ? '.NET Runtime' :
                        'ASP.NET Core'}.`);
                this.appendOutputLine('');
                if (((_b = (_a = this.inProgressDownloads) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 1) {
                    // Already a download in progress
                    this.appendOutputLine(` -- Concurrent download of '${acquisitionStarted.install.installId}' started!`);
                    this.appendOutputLine('');
                }
                else {
                    this.startDownloadIndicator();
                }
                const startVersionString = this.inProgressDownloads.join(', ');
                this.appendOutput(`Downloading .NET version(s) ${startVersionString} ...`);
                break;
            case EventType_1.EventType.DotnetAcquisitionCompleted:
                const acquisitionCompleted = event;
                this.appendOutputLine(' Done!');
                this.appendOutputLine(`.NET ${acquisitionCompleted.install.installId} executable path: ${acquisitionCompleted.dotnetPath}`);
                this.appendOutputLine('');
                this.inProgressVersionDone(acquisitionCompleted.install.installId);
                if (((_d = (_c = this.inProgressDownloads) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0) {
                    const completedVersionString = `'${this.inProgressDownloads.join('\', \'')}'`;
                    this.appendOutput(`Still downloading .NET version(s) ${completedVersionString} ...`);
                }
                else {
                    this.stopDownloadIndicator();
                }
                break;
            case EventType_1.EventType.DotnetAcquisitionSuccessEvent:
                if (event instanceof EventStreamEvents_1.DotnetExistingPathResolutionCompleted) {
                    this.appendOutput(`Using configured .NET path: ${event.resolvedPath}\n`);
                }
                break;
            case EventType_1.EventType.DotnetVisibleWarning:
                this.appendOutputLine('');
                this.appendOutputLine(event.eventMessage);
                this.appendOutputLine('');
                break;
            case EventType_1.EventType.DotnetAcquisitionAlreadyInstalled:
                if (event instanceof EventStreamEvents_1.DotnetAcquisitionAlreadyInstalled) {
                    const extensionId = event.requestingExtensionId;
                    if (extensionId !== StringConstants_1.AUTOMATIC_UPDATE_EXTENSION_ID) // automatic update will try to update existing installs - no need to print the message.
                     {
                        this.appendOutput(`${extensionId}: Trying to install .NET ${event.install.installId} but it already exists. No downloads or changes were made.\n`);
                    }
                }
                break;
            case EventType_1.EventType.DotnetAcquisitionInProgress:
                if (event instanceof EventStreamEvents_1.DotnetAcquisitionInProgress) {
                    this.appendOutput(`${event.requestingExtensionId} tried to install .NET ${event.install.installId} but that install had already been requested. No downloads or changes were made.\n`);
                }
                break;
            case (EventType_1.EventType.DotnetAcquisitionError, EventType_1.EventType.DotnetAcquisitionFinalError):
                const error = event;
                this.appendOutputLine(`\nError : (${(_e = error === null || error === void 0 ? void 0 : error.eventName) !== null && _e !== void 0 ? _e : ''})`);
                if (this.inProgressDownloads.includes((_g = (_f = error === null || error === void 0 ? void 0 : error.install) === null || _f === void 0 ? void 0 : _f.installId) !== null && _g !== void 0 ? _g : '')) {
                    this.appendOutputLine(`Failed to download .NET ${(_h = error === null || error === void 0 ? void 0 : error.install) === null || _h === void 0 ? void 0 : _h.installId}:`);
                    this.appendOutputLine((_j = error === null || error === void 0 ? void 0 : error.error) === null || _j === void 0 ? void 0 : _j.message);
                    this.appendOutputLine('');
                    this.updateDownloadIndicators((_k = error.install) === null || _k === void 0 ? void 0 : _k.installId);
                }
                break;
            case EventType_1.EventType.DotnetInstallExpectedAbort:
                const abortEvent = event;
                this.appendOutputLine(`Cancelled Installation of .NET ${(_l = abortEvent.install) === null || _l === void 0 ? void 0 : _l.installId}.`);
                this.appendOutputLine(abortEvent.error.message);
                this.updateDownloadIndicators((_m = abortEvent.install) === null || _m === void 0 ? void 0 : _m.installId);
                break;
            case EventType_1.EventType.DotnetUpgradedEvent:
                const upgradeMessage = event;
                this.appendOutputLine(`${upgradeMessage.eventMessage}:`);
                break;
            case EventType_1.EventType.OfflineInstallUsed:
                const offlineUsedMsg = event;
                this.appendOutputLine(offlineUsedMsg.eventMessage);
                break;
            case EventType_1.EventType.OfflineWarning:
                const offlineWarning = event;
                this.appendOutputLine(offlineWarning.eventMessage);
                break;
            case EventType_1.EventType.DotnetUninstallMessage:
                const uninstallMessage = event;
                this.appendOutputLine(uninstallMessage.eventMessage);
                break;
            case EventType_1.EventType.FeedInjectionMessage:
                const feedMessage = event;
                this.appendOutputLine(feedMessage.eventMessage);
                break;
        }
    }
    appendOutput(output) {
        this.outputChannel.append(output);
        this.hasContent = true;
    }
    appendOutputLine(output) {
        this.outputChannel.appendLine(output);
        this.hasContent = true;
    }
    showOutput(preserveFocus = true) {
        if (this.hasContent) {
            this.outputChannel.show(preserveFocus);
        }
    }
    dispose() {
        // Nothing to dispose
    }
    updateDownloadIndicators(installId) {
        var _a, _b;
        if (installId && installId !== 'null') {
            this.inProgressVersionDone(installId);
        }
        if (((_b = (_a = this.inProgressDownloads) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0) {
            const errorVersionString = this.inProgressDownloads.join(', ');
            this.appendOutput(`Still downloading .NET version(s) ${errorVersionString} ...`);
        }
        else {
            this.stopDownloadIndicator();
        }
    }
    startDownloadIndicator() {
        this.downloadProgressInterval = setInterval(() => this.appendOutput('.'), 3000);
    }
    stopDownloadIndicator() {
        if (this.downloadProgressInterval) {
            clearTimeout(this.downloadProgressInterval);
            this.downloadProgressInterval = undefined;
        }
    }
    inProgressVersionDone(version) {
        const index = this.inProgressDownloads.indexOf(version);
        this.inProgressDownloads.splice(index, 1);
    }
}
exports.OutputChannelObserver = OutputChannelObserver;
//# sourceMappingURL=OutputChannelObserver.js.map